import { MOCK_COLLECTION_RECEIPTS, MOCK_LATE_FEE_APPLICATIONS } from "@/mocks/collections";
import { listGroups } from "@/services/groups";
import { listMembers } from "@/services/members";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import {
  ApiError,
  type CollectionDashboardData,
  type CollectionDemand,
  type CollectionReceipt,
  type CycleCollectionView,
  type DemandPayment,
  type DemandStatus,
  type GroupCollectionRow,
  type KametiGroup,
  type LateFeeApplication,
  type Member,
  type PaymentMode,
  type ReceiptView,
  type RecordPaymentInput,
} from "@/services/types";
import { computeLateFee, cycleDate } from "@/utils/collection";
import { computeCommission, isSameDay } from "@/utils/auction";
import { cyclePeriodLabel, nextAuctionDate } from "@/utils/group";

/**
 * Centralized collection service. Every collection screen calls through here —
 * no screen performs a direct data fetch. Demands are derived from the groups'
 * closed cycles (mirroring the auction module's arithmetic) and paid state is
 * aggregated from receipts. Receipts are never deleted — only reversed with a
 * mandatory reason. The real REST API swaps in behind these signatures later.
 */

const delay = (ms = 350) => new Promise((resolve) => setTimeout(resolve, ms));

let receiptStore: CollectionReceipt[] = [...MOCK_COLLECTION_RECEIPTS];
let feeStore: LateFeeApplication[] = [...MOCK_LATE_FEE_APPLICATIONS];
let seqNum = MOCK_COLLECTION_RECEIPTS.length + 1;

function startOfToday(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function nextReceiptNumber(): string {
  const now = new Date();
  const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  return `RCP-${ymd}-${String(seqNum++).padStart(3, "0")}`;
}

/** Per-member dividend credit and net installment for a decided cycle. */
function cycleFinancials(group: KametiGroup, cycle: number): { dividend: number; commission: number; net: number; wonMemberId: string } | null {
  const won = group.wonCycles.find((w) => w.cycle === cycle);
  if (!won) return null;
  const isReserved = cycle === group.reservedCycle;
  const discount = isReserved ? 0 : Math.max(0, group.chitValue - won.winningBid);
  const dividend = isReserved ? 0 : (group.memberCount > 0 ? Math.floor(discount / group.memberCount) : 0);
  const baseAfterDiv = Math.max(0, group.monthlyInstallment - dividend);
  const commission = computeCommission(
    group.commissionType,
    group.commissionValue,
    group.chitValue,
    discount
  );
  const commPerMember = group.memberCount > 0 ? Math.floor(commission / group.memberCount) : 0;
  return { dividend, commission: commPerMember, net: baseAfterDiv + commPerMember, wonMemberId: won.memberId };
}

/** Sum of non-reversed payments per demand id. */
function paidByDemand(): Map<string, number> {
  const map = new Map<string, number>();
  for (const r of receiptStore) {
    if (r.reversedAt) continue;
    for (const p of r.payments) {
      map.set(p.demandId, (map.get(p.demandId) ?? 0) + p.amount);
    }
  }
  return map;
}

/** Effective (non-waived) late fee per demand id. */
function feeByDemand(): Map<string, { amount: number; id: string; appliedAt: string; appliedBy: string; reason: string }> {
  const map = new Map<string, { amount: number; id: string; appliedAt: string; appliedBy: string; reason: string }>();
  for (const f of feeStore) {
    if (f.waivedAt) continue;
    const existing = map.get(f.demandId);
    if (!existing) map.set(f.demandId, { amount: f.amount, id: f.id, appliedAt: f.appliedAt, appliedBy: f.appliedBy, reason: f.reason });
    else map.set(f.demandId, { ...existing, amount: existing.amount + f.amount });
  }
  return map;
}

/**
 * Every demand across all active groups' closed cycles, computed from the
 * mock store. Stays mock-only regardless of USE_MOCK_API — reports.ts,
 * ledger.ts and payouts.ts (later stages, not yet wired to the real API)
 * depend on it, the same "always mock" pattern as services/groups.ts's
 * getGroupMockSnapshot. Real-mode collection screens use the functions
 * below instead, which hit the real endpoints directly.
 */
export async function buildAllDemands(): Promise<CollectionDemand[]> {
  const [groups, members] = await Promise.all([listGroups(), listMembers()]);
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const paidMap = paidByDemand();
  const feeMap = feeByDemand();
  const today = startOfToday();
  const out: CollectionDemand[] = [];

  for (const group of groups) {
    if (group.status !== "active") continue;
    for (const won of group.wonCycles) {
      const fin = cycleFinancials(group, won.cycle);
      if (!fin) continue;
      for (const ticket of group.tickets) {
        const member = memberMap.get(ticket.memberId);
        if (!member) continue;
        const id = `${group.id}-c${won.cycle}-${ticket.memberId}`;
        const fee = feeMap.get(id)?.amount ?? 0;
        const netPayable = fin.net + fee;
        const paid = Math.round((paidMap.get(id) ?? 0) * 100) / 100;
        const balance = Math.round((netPayable - paid) * 100) / 100;
        const dueDate = cycleDate(group.startDate, won.cycle, group.dueDay);
        const rawOverdue = Math.floor((today.getTime() - dueDate.getTime()) / 86400000);
        const daysOverdue = balance > 0 ? Math.max(0, rawOverdue) : 0;
        const applicableLateFee =
          balance > 0 && daysOverdue > 0 ? Math.round(computeLateFee(group, fin.net, daysOverdue) * 100) / 100 : 0;
        const status: CollectionDemand["status"] =
          balance <= 0 ? "paid" : paid > 0 ? "partial" : "pending";
        const matchingReceipt = receiptStore
          .filter((r) => !r.reversedAt && r.payments.some((p) => p.demandId === id))
          .sort((a, b) => b.receivedOn.localeCompare(a.receivedOn))[0];

        out.push({
          id,
          groupId: group.id,
          groupCode: group.groupCode,
          groupName: group.name,
          cycle: won.cycle,
          periodLabel: cyclePeriodLabel(group.startDate, won.cycle),
          memberId: member.id,
          memberCode: member.memberCode,
          memberName: member.fullName,
          memberPhone: member.primaryPhone,
          ticketNumber: ticket.ticketNumber,
          baseInstallment: group.monthlyInstallment,
          dividendCredit: fin.dividend,
          commission: fin.commission,
          lateFee: fee,
          netPayable,
          paid,
          balance,
          status,
          dueDate: dueDate.toISOString(),
          daysOverdue,
          applicableLateFee,
          hasLateFeeApplied: fee > 0,
          wonThisCycle: fin.wonMemberId === member.id,
          lastReceiptId: matchingReceipt?.id ?? null,
          lastReceiptNo: matchingReceipt?.receiptNumber ?? null,
        });
      }
    }
  }
  return out;
}

async function buildReceiptViews(): Promise<ReceiptView[]> {
  const [members, groups, demands] = await Promise.all([listMembers(), listGroups(), buildAllDemands()]);
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const groupMap = new Map(groups.map((g) => [g.id, g]));
  const demandMap = new Map(demands.map((d) => [d.id, d]));

  return receiptStore
    .map((r) => {
      const member = memberMap.get(r.memberId);
      let remainingBalance = 0;
      const firstDemand = r.payments[0] ? demandMap.get(r.payments[0].demandId) : null;
      const firstGroup = firstDemand ? groupMap.get(firstDemand.groupId) : null;
      const parsedTicketNo = firstDemand?.ticketNumber ? parseInt(firstDemand.ticketNumber, 10) : undefined;
      const lines = r.payments.map((p) => {
        const d = demandMap.get(p.demandId);
        if (d) remainingBalance += d.balance;
        const g = d ? groupMap.get(d.groupId) : null;
        const lineTicketNo = d?.ticketNumber ? parseInt(d.ticketNumber, 10) : undefined;
        return {
          groupName: g?.name ?? "—",
          groupCode: g?.groupCode ?? "—",
          cycle: d?.cycle ?? 0,
          periodLabel: d?.periodLabel ?? "—",
          amount: p.amount,
          ticketNo: Number.isFinite(lineTicketNo) ? lineTicketNo : undefined,
        };
      });
      return {
        ...r,
        memberName: member?.fullName ?? "—",
        memberCode: member?.memberCode ?? "—",
        memberPhone: member?.primaryPhone ?? "—",
        startDate: firstGroup?.startDate,
        chitValue: firstGroup?.chitValue,
        groupInstallmentAmount: firstGroup?.installmentAmount,
        totalMembers: firstGroup?.totalMembers,
        groupCode: firstGroup?.groupCode,
        groupName: firstGroup?.name,
        ticketNo: Number.isFinite(parsedTicketNo) ? parsedTicketNo : undefined,
        lines,
        remainingBalance: Math.round(remainingBalance * 100) / 100,
      };
    })
    .sort((a, b) => b.receivedOn.localeCompare(a.receivedOn));
}

/* ================= Real API — backend shapes and mapping ================= */

interface BackendInstallmentRow {
  id: string;
  cycleId: string;
  groupMemberId: string;
  memberId?: string;
  baseAmount: string;
  dividendCredit: string;
  commissionAmount?: string;
  lateFee: string;
  netPayable: string;
  amountPaid: string;
  balance: string;
  dueDate: string;
  status: string;
  ticketNo?: number;
  memberCode?: string;
  fullName?: string;
  phone?: string;
  groupId?: string;
  groupCode?: string;
  groupName?: string;
  cycleNo?: number;
  periodLabel?: string;
  accountBalance?: string | number;
  applicableLateFee?: string;
  previousPendingDues?: {
    installmentId: string;
    cycleId: string;
    cycleNo: number;
    periodLabel: string;
    baseAmount: string | number;
    lateFee: string | number;
    netPayable: string | number;
    amountPaid: string | number;
    balance: string | number;
    dueDate: string;
    status: string;
  }[];
  totalPreviousPendingDues?: string;
  lastReceiptId?: string | null;
  lastReceiptNo?: string | null;
  payments?: {
    paymentId: string;
    receiptNo: string;
    amount: string | number;
    mode: string;
    paidAt?: string;
    createdAt?: string;
  }[];
}

interface BackendDashboardRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  totalMembers: number;
  cycleId: string;
  cycleNo: number;
  periodLabel: string;
  cycleStatus: string;
  auctionHeld: boolean;
  expectedCollection: string | null;
  actualCollected: string;
  pendingCount: number;
  nextAuctionDate?: string | null;
}

interface BackendCollectionSheet {
  cycleId: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycleNo: number;
  periodLabel: string;
  dueDate: string;
  status: string;
  commissionAmount?: string;
  installments: BackendInstallmentRow[];
  totals: { totalDemand: string; totalCollected: string; totalPending: string; totalCommission?: string; pendingCount: number };
}

interface BackendGroupCycleRow {
  id: string;
  cycleNo: number;
  status: string;
  periodLabel: string;
}

interface BackendPayment {
  id: string;
  receiptNo: string;
  memberId: string;
  groupId: string | null;
  amount: string;
  mode: string;
  referenceNo: string | null;
  receivedOn: string;
  collectedBy: string | null;
  isReversed: boolean;
  reversalReason: string | null;
  remarks: string | null;
  createdAt: string;
}

/** GET /payments' list meta — subtotals are pre-summed server-side over the whole filtered range (reversed payments excluded), not just the current page. */
interface BackendPaymentsMeta {
  subtotals: Record<string, string>;
}

function sumSubtotals(meta: BackendPaymentsMeta): number {
  return Object.values(meta.subtotals).reduce((sum, v) => sum + Number(v), 0);
}

interface BackendReceipt extends BackendPayment {
  memberCode: string;
  fullName: string;
  phone: string;
  fatherOrSpouse?: string | null;
  groupCode: string | null;
  groupName: string | null;
  startDate?: string | null;
  chitValue?: string | number | null;
  groupInstallmentAmount?: string | number | null;
  totalMembers?: number | null;
  ticketNo?: number | null;
  collectedByName: string | null;
  collectedByRole?: string | null;
  remainingBalance?: string | number | null;
  allocations: {
    installmentId: string;
    cycleNo: number;
    periodLabel: string;
    amount: string;
    baseAmount?: string | number | null;
    dividendCredit?: string | number | null;
    lateFee?: string | number | null;
    netPayable?: string | number | null;
    discountAmount?: string | number | null;
    dividendPerMember?: string | number | null;
    commissionAmount?: string | number | null;
    ticketNo?: number | null;
  }[];
  amountInWords: string;
}

interface BackendLateFeeRow {
  installmentId: string;
  groupMemberId: string;
  groupId?: string;
  cycleNo?: number;
  ticketNo: number;
  memberCode: string;
  fullName: string;
  groupCode: string;
  groupName: string;
  dueDate: string;
  daysLate: number;
  balance: string;
  currentLateFee: string;
  calculatedLateFee: string;
}

const PAYMENT_MODE_TO_BACKEND: Record<PaymentMode, string> = {
  cash: "CASH",
  upi: "UPI",
  bank_transfer: "BANK_TRANSFER",
  cheque: "CHEQUE",
  member_account: "MEMBER_ACCOUNT",
};
const PAYMENT_MODE_FROM_BACKEND: Record<string, PaymentMode> = {
  CASH: "cash",
  UPI: "upi",
  BANK_TRANSFER: "bank_transfer",
  CHEQUE: "cheque",
  MEMBER_ACCOUNT: "member_account",
};

/**
 * Local "YYYY-MM-DD" — never .toISOString(), which shifts the calendar day
 * in timezones behind UTC. A string already in this shape passes through
 * untouched (no re-parsing needed); anything else (a full ISO datetime, a
 * legacy default value) is read back via local getters, matching
 * DatePicker.tsx's own fix.
 */
function toDateOnlyString(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function todayDateOnly(): string {
  return toDateOnlyString(new Date().toISOString());
}

/** Local calendar-day difference — never a UTC round trip. */
function daysBetween(fromDateOnly: string, toDateOnly: string): number {
  const [fy, fm, fd] = fromDateOnly.split("-").map(Number);
  const [ty, tm, td] = toDateOnly.split("-").map(Number);
  const from = new Date(fy, fm - 1, fd);
  const to = new Date(ty, tm - 1, td);
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function demandStatusFromBackend(status: string, paid: number): DemandStatus {
  if (status === "PAID" || status === "WAIVED") return "paid";
  if (paid > 0) return "partial";
  return "pending";
}

/** Maps one installment row into a CollectionDemand — used by the collection sheet and cross-group pending-demands, where group/cycle context comes from the surrounding response rather than the row itself. */
function mapDemand(
  row: BackendInstallmentRow,
  ctx: { memberId: string; groupId: string; groupCode: string; groupName: string; cycleNo: number; periodLabel: string }
): CollectionDemand {
  const netPayable = Number(row.netPayable);
  const paid = Number(row.amountPaid);
  const balance = Number(row.balance);
  const status = demandStatusFromBackend(row.status, paid);
  const daysOverdue = row.status === "OVERDUE" ? Math.max(0, daysBetween(row.dueDate, todayDateOnly())) : 0;

  return {
    id: row.id,
    groupId: ctx.groupId,
    groupCode: ctx.groupCode,
    groupName: ctx.groupName,
    cycle: ctx.cycleNo,
    periodLabel: ctx.periodLabel,
    memberId: ctx.memberId,
    memberCode: row.memberCode ?? "",
    memberName: row.fullName ?? "",
    memberPhone: row.phone ?? "",
    ticketNumber: row.ticketNo != null ? String(row.ticketNo) : "",
    baseInstallment: Math.max(0, Number(row.baseAmount) || 0),
    dividendCredit: Math.max(0, Number(row.dividendCredit) || 0),
    commission: Math.max(0, row.commissionAmount != null ? Number(row.commissionAmount) : 0),
    lateFee: Math.max(0, Number(row.lateFee) || 0),
    netPayable,
    paid,
    balance,
    accountBalance: row.accountBalance != null ? Number(row.accountBalance) : undefined,
    status,
    dueDate: row.dueDate,
    daysOverdue: Math.max(0, daysOverdue || 0),
    applicableLateFee: Math.max(0, row.applicableLateFee != null ? Number(row.applicableLateFee) : 0),
    hasLateFeeApplied: Math.max(0, Number(row.lateFee) || 0) > 0,
    wonThisCycle: false,
    groupMemberId: row.groupMemberId,
    previousPendingDues: row.previousPendingDues ?? [],
    totalPreviousPendingDues: row.totalPreviousPendingDues != null ? Number(row.totalPreviousPendingDues) : undefined,
    lastReceiptId: row.lastReceiptId ?? null,
    lastReceiptNo: row.lastReceiptNo ?? null,
    payments: (row.payments ?? []).map((p) => ({
      paymentId: p.paymentId,
      receiptNo: p.receiptNo,
      amount: Number(p.amount) || 0,
      mode: PAYMENT_MODE_FROM_BACKEND[p.mode] ?? "cash",
      paidAt: p.paidAt,
      createdAt: p.createdAt,
    })),
  };
}

function mapReceipt(r: BackendReceipt): ReceiptView {
  return {
    id: r.id,
    receiptNumber: r.receiptNo,
    memberId: r.memberId,
    payments: (r.allocations ?? []).map((a) => ({ demandId: a.installmentId, amount: Number(a.amount) })),
    total: Number(r.amount),
    mode: PAYMENT_MODE_FROM_BACKEND[r.mode] ?? "cash",
    reference: r.referenceNo ?? "",
    receivedOn: r.receivedOn,
    remarks: r.remarks ?? "",
    collectedBy: r.collectedByName ?? "Staff",
    createdAt: r.createdAt,
    reversedAt: r.isReversed ? r.createdAt : null,
    reversalReason: r.reversalReason ?? null,
    memberName: r.fullName,
    memberCode: r.memberCode,
    memberPhone: r.phone,
    fatherOrSpouse: r.fatherOrSpouse ?? null,
    startDate: r.startDate ?? undefined,
    chitValue: r.chitValue != null ? Number(r.chitValue) : undefined,
    groupInstallmentAmount: r.groupInstallmentAmount != null ? Number(r.groupInstallmentAmount) : undefined,
    totalMembers: r.totalMembers ?? undefined,
    groupCode: r.groupCode ?? undefined,
    groupName: r.groupName ?? undefined,
    collectedByRole: r.collectedByRole ?? null,
    ticketNo: r.ticketNo != null ? Number(r.ticketNo) : (r.allocations?.[0]?.ticketNo != null ? Number(r.allocations[0].ticketNo) : undefined),
    lines: (r.allocations ?? []).map((a) => ({
      groupName: r.groupName ?? "—",
      groupCode: r.groupCode ?? "—",
      cycle: a.cycleNo,
      periodLabel: a.periodLabel,
      amount: Number(a.amount),
      baseAmount: a.baseAmount != null ? Number(a.baseAmount) : undefined,
      dividendCredit: a.dividendCredit != null ? Number(a.dividendCredit) : undefined,
      lateFee: a.lateFee != null ? Number(a.lateFee) : undefined,
      netPayable: a.netPayable != null ? Number(a.netPayable) : undefined,
      discountAmount: a.discountAmount != null ? Number(a.discountAmount) : undefined,
      dividendPerMember: a.dividendPerMember != null ? Number(a.dividendPerMember) : undefined,
      commissionAmount: a.commissionAmount != null ? Number(a.commissionAmount) : undefined,
      ticketNo: a.ticketNo ?? undefined,
    })),
    amountInWords: r.amountInWords,
    remainingBalance: r.remainingBalance != null ? Number(r.remainingBalance) : 0,
  };
}

async function fetchReceiptView(paymentId: string): Promise<ReceiptView> {
  const r = await http<BackendReceipt>(`/payments/${paymentId}/receipt`, { method: "GET" });
  return mapReceipt(r);
}

async function fetchCycleRow(groupId: string, cycle: number): Promise<BackendGroupCycleRow> {
  const rows = await http<BackendGroupCycleRow[]>(`/groups/${groupId}/cycles`, { method: "GET" });
  const row = rows.find((c) => c.cycleNo === cycle);
  if (!row) throw new ApiError("Cycle not found.", 404, "CYCLE_NOT_FOUND");
  return row;
}

/* ================= Dashboard ================= */

export async function getCollectionDashboard(): Promise<CollectionDashboardData> {
  if (!USE_MOCK_API) {
    const [rows, receiptsRes] = await Promise.all([
      http<BackendDashboardRow[]>("/collection/dashboard", { method: "GET" }),
      http<{ data: BackendPayment[]; meta: BackendPaymentsMeta }>(
        `/payments?dateFrom=${todayDateOnly()}&dateTo=${todayDateOnly()}&pageSize=100`,
        { method: "GET", raw: true }
      ),
    ]);

    const groupRows: GroupCollectionRow[] = rows
      .filter((r) => r.auctionHeld)
      .map((r) => {
        const collected = Number(r.actualCollected);
        const expected = Number(r.expectedCollection ?? 0);
        const nextCycle = r.cycleNo + 1;
        return {
          groupId: r.groupId,
          groupCode: r.groupCode,
          groupName: r.groupName,
          cycle: r.cycleNo,
          periodLabel: r.periodLabel,
          demandPerMember: r.totalMembers > 0 ? Math.round((expected / r.totalMembers) * 100) / 100 : 0,
          paidMembers: r.totalMembers - r.pendingCount,
          totalMembers: r.totalMembers,
          collected,
          expected,
          nextCycle,
          nextAuctionPending: nextCycle <= r.totalMembers,
          nextAuctionDate: r.nextAuctionDate ?? null,
          isComplete: nextCycle > r.totalMembers,
        };
      });

    const todayPayments = receiptsRes.data;
    const todayReceipts = (todayPayments as unknown as BackendReceipt[]).map(mapReceipt);
    const todayCollection = sumSubtotals(receiptsRes.meta as BackendPaymentsMeta);

    // The list endpoint's meta.subtotals is already summed server-side over
    // the whole filtered range (excluding reversed payments), not just the
    // current page — no need to paginate through every payment row just to
    // total a month that could exceed the 200-per-page cap.
    const monthStartStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`;
    const monthRes = await http<{ meta: BackendPaymentsMeta }>(
      `/payments?dateFrom=${monthStartStr}&dateTo=${todayDateOnly()}&pageSize=1`,
      { method: "GET", raw: true }
    );
    const monthCollection = sumSubtotals(monthRes.meta);

    const totalOutstanding = rows.reduce((s, r) => s + Math.max(0, Number(r.expectedCollection ?? 0) - Number(r.actualCollected)), 0);
    const overdueCount = rows.reduce((s, r) => s + (r.auctionHeld ? r.pendingCount : 0), 0);

    return {
      todayCollection: Math.round(todayCollection * 100) / 100,
      monthCollection: Math.round(monthCollection * 100) / 100,
      totalOutstanding: Math.round(totalOutstanding * 100) / 100,
      overdueCount,
      groups: groupRows.sort((a, b) => a.groupName.localeCompare(b.groupName)),
      todayReceipts: todayReceipts.sort((a, b) => b.receivedOn.localeCompare(a.receivedOn)),
    };
  }

  await delay();
  const [demands, receipts] = await Promise.all([buildAllDemands(), buildReceiptViews()]);
  const groups = await listGroups();
  const memberMap = new Map((await listMembers()).map((m) => [m.id, m]));

  const today = startOfToday();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const groupRows: GroupCollectionRow[] = [];
  for (const group of groups) {
    if (group.status !== "active") continue;
    const decided = [...group.wonCycles].sort((a, b) => a.cycle - b.cycle);
    if (decided.length === 0) continue;
    const latest = decided[decided.length - 1].cycle;
    const latestDemands = demands.filter((d) => d.groupId === group.id && d.cycle === latest);
    if (latestDemands.length === 0) continue;
    const collected = latestDemands.reduce((s, d) => s + d.paid, 0);
    const expected = latestDemands.reduce((s, d) => s + d.netPayable, 0);
    const paidMembers = latestDemands.filter((d) => d.paid >= d.netPayable).length;
    const nextCycle = latest + 1;
    const nextAuctionPending = nextCycle <= group.memberCount;
    const organizer = memberMap.get(group.organizerMemberId);
    groupRows.push({
      groupId: group.id,
      groupCode: group.groupCode,
      groupName: group.name,
      cycle: latest,
      periodLabel: cyclePeriodLabel(group.startDate, latest),
      demandPerMember: latestDemands[0].netPayable,
      paidMembers,
      totalMembers: latestDemands.length,
      collected: Math.round(collected * 100) / 100,
      expected: Math.round(expected * 100) / 100,
      nextCycle,
      nextAuctionPending,
      nextAuctionDate: nextAuctionPending ? nextAuctionDate(group) : null,
      isComplete: nextCycle > group.memberCount,
    });
    void organizer;
  }

  const todayReceipts = receipts.filter((r) => isSameDay(r.receivedOn, today));
  const todayCollection = todayReceipts.filter((r) => !r.reversedAt).reduce((s, r) => s + r.total, 0);
  const monthCollection = receipts
    .filter((r) => !r.reversedAt && new Date(r.receivedOn) >= monthStart)
    .reduce((s, r) => s + r.total, 0);
  const totalOutstanding = demands.reduce((s, d) => s + d.balance, 0);
  const overdueCount = demands.filter((d) => d.balance > 0 && d.daysOverdue > 0).length;

  return {
    todayCollection: Math.round(todayCollection * 100) / 100,
    monthCollection: Math.round(monthCollection * 100) / 100,
    totalOutstanding: Math.round(totalOutstanding * 100) / 100,
    overdueCount,
    groups: groupRows.sort((a, b) => a.groupName.localeCompare(b.groupName)),
    todayReceipts,
  };
}

/* ================= Collection sheet ================= */

export async function getCycleCollection(groupId: string, cycle?: number): Promise<CycleCollectionView> {
  if (!USE_MOCK_API) {
    const cycleRows = await http<BackendGroupCycleRow[]>(`/groups/${groupId}/cycles`, { method: "GET" });
    if (cycleRows.length === 0) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");

    const currentRow = cycleRows.find((c) => c.status !== "CLOSED") ?? cycleRows[cycleRows.length - 1];
    const selectedRow = cycle != null ? cycleRows.find((c) => c.cycleNo === cycle) ?? currentRow : currentRow;

    const [group, sheet] = await Promise.all([
      http<{ groupCode: string; groupName: string; installmentAmount: string; organizerCycleNo: number }>(`/groups/${groupId}`, {
        method: "GET",
      }),
      http<BackendCollectionSheet>(`/cycles/${selectedRow.id}/installments`, { method: "GET" }),
    ]);

    const auctionPending = selectedRow.status === "PENDING" || selectedRow.status === "AUCTION_OPEN";
    const demands = sheet.installments.map((row) =>
      mapDemand(row, {
        memberId: row.memberId ?? "",
        groupId,
        groupCode: group.groupCode,
        groupName: group.groupName,
        cycleNo: selectedRow.cycleNo,
        periodLabel: selectedRow.periodLabel,
      })
    );

    const commAmount = sheet.commissionAmount != null ? Number(sheet.commissionAmount) : 0;
    const commPerMember = demands[0]?.commission ?? (demands.length ? commAmount / demands.length : 0);

    return {
      groupId,
      groupCode: group.groupCode,
      groupName: group.groupName,
      monthlyInstallment: Number(group.installmentAmount),
      cycleId: selectedRow.id,
      status: selectedRow.status,
      cycle: selectedRow.cycleNo,
      periodLabel: selectedRow.periodLabel,
      isOrganizerReserved: selectedRow.cycleNo === group.organizerCycleNo,
      auctionPending,
      demandPerMember: demands[0]?.netPayable ?? Number(group.installmentAmount),
      commissionAmount: commAmount,
      commissionPerMember: commPerMember,
      totalExpected: Number(sheet.totals.totalDemand),
      totalCollected: Number(sheet.totals.totalCollected),
      balance: Number(sheet.totals.totalPending),
      demands,
      availableCycles: cycleRows.map((c) => ({ cycle: c.cycleNo, periodLabel: c.periodLabel })),
    };
  }

  await delay(200);
  const groups = await listGroups();
  const group = groups.find((g) => g.id === groupId);
  if (!group) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");

  const decided = new Set(group.wonCycles.map((w) => w.cycle));
  const currentCycle = group.wonCycles.length + 1;
  const selected = cycle ?? currentCycle;

  const availableCycles = group.wonCycles
    .map((w) => ({ cycle: w.cycle, periodLabel: cyclePeriodLabel(group.startDate, w.cycle) }))
    .concat(
      currentCycle <= group.memberCount
        ? [{ cycle: currentCycle, periodLabel: cyclePeriodLabel(group.startDate, currentCycle) }]
        : []
    )
    .sort((a, b) => a.cycle - b.cycle);

  const auctionPending = !decided.has(selected);
  const demands = auctionPending
    ? []
    : (await buildAllDemands()).filter((d) => d.groupId === groupId && d.cycle === selected);
  const isReserved = selected === group.reservedCycle;

  const totalExpected = demands.reduce((s, d) => s + d.netPayable, 0);
  const totalCollected = demands.reduce((s, d) => s + d.paid, 0);
  const mockComm = demands.reduce((s, d) => s + (d.commission ?? 0), 0);

  return {
    groupId: group.id,
    groupCode: group.groupCode,
    groupName: group.name,
    monthlyInstallment: group.monthlyInstallment,
    cycleId: `mock-cycle-${group.id}-${selected}`,
    cycle: selected,
    periodLabel: cyclePeriodLabel(group.startDate, selected),
    isOrganizerReserved: isReserved,
    auctionPending,
    demandPerMember: demands[0]?.netPayable ?? group.monthlyInstallment,
    commissionAmount: mockComm,
    commissionPerMember: demands[0]?.commission ?? 0,
    totalExpected: Math.round(totalExpected * 100) / 100,
    totalCollected: Math.round(totalCollected * 100) / 100,
    balance: Math.round((totalExpected - totalCollected) * 100) / 100,
    demands,
    availableCycles,
  };
}

/* ================= Receipts ================= */

export async function getMemberPendingDemands(memberId: string): Promise<CollectionDemand[]> {
  if (!USE_MOCK_API) {
    const rows = await http<BackendInstallmentRow[]>(`/members/${memberId}/pending-demands`, { method: "GET" });
    return rows
      .map((row) =>
        mapDemand(row, {
          memberId,
          groupId: row.groupId ?? "",
          groupCode: row.groupCode ?? "",
          groupName: row.groupName ?? "",
          cycleNo: row.cycleNo ?? 0,
          periodLabel: row.periodLabel ?? "",
        })
      )
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }

  await delay(200);
  const demands = await buildAllDemands();
  return demands
    .filter((d) => d.memberId === memberId && d.balance > 0)
    .sort((a, b) => a.groupId.localeCompare(b.groupId) || a.cycle - b.cycle);
}

export async function recordPayment(input: RecordPaymentInput): Promise<{ receipts: ReceiptView[]; pending: CollectionDemand[] }> {
  if (!USE_MOCK_API) {
    if (!input.payments.length) throw new ApiError("No payment amount provided.", 400, "NO_PAYMENT");
    if (input.receivedOn && new Date(toDateOnlyString(input.receivedOn)) > new Date(todayDateOnly())) {
      throw new ApiError("Payments cannot be recorded against a future date.", 409, "FUTURE_DATE");
    }

    // A real receipt is scoped to one group_member — a cross-group selection
    // (the standalone receipt-entry screen shows every group a member owes)
    // becomes one backend call per group, in the order the demands were
    // selected (oldest-due-first, matching how the sheet/pending-demands
    // lists are already ordered).
    const byGroupMember = new Map<string, string[]>();
    const amountByGroupMember = new Map<string, number>();
    for (const p of input.payments) {
      if (p.amount <= 0) continue;
      if (!p.groupMemberId) {
        throw new ApiError("This demand is missing its enrollment reference.", 422, "VALIDATION_ERROR");
      }
      const ids = byGroupMember.get(p.groupMemberId) ?? [];
      ids.push(p.demandId);
      byGroupMember.set(p.groupMemberId, ids);
      amountByGroupMember.set(p.groupMemberId, (amountByGroupMember.get(p.groupMemberId) ?? 0) + p.amount);
    }
    if (byGroupMember.size === 0) throw new ApiError("No payment amount provided.", 400, "NO_PAYMENT");

    const receivedOn = input.receivedOn ? toDateOnlyString(input.receivedOn) : todayDateOnly();
    const backendMode = PAYMENT_MODE_TO_BACKEND[input.mode];

    const receipts: ReceiptView[] = [];
    for (const [groupMemberId, installmentIds] of byGroupMember) {
      const payment = await http<BackendPayment>("/payments", {
        method: "POST",
        headers: { "Idempotency-Key": `payment-${groupMemberId}-${Date.now()}-${Math.random().toString(36).slice(2)}` },
        body: {
          groupMemberId,
          amount: amountByGroupMember.get(groupMemberId),
          mode: backendMode,
          referenceNo: input.reference.trim() || undefined,
          receivedOn,
          installmentIds,
          remarks: input.remarks.trim() || undefined,
          applyLateFee: input.applyLateFee,
        },
      });
      receipts.push(await fetchReceiptView(payment.id));
    }

    const pending = await getMemberPendingDemands(input.memberId);
    return { receipts, pending };
  }

  await delay(450);
  if (!input.payments.length) throw new ApiError("No payment amount provided.", 400, "NO_PAYMENT");

  const shouldApplyLateFee = (demandId: string) => {
    if (input.applyLateFee === false) return false;
    if (input.applyLateFee === true) return true;
    if (Array.isArray(input.applyLateFee)) return input.applyLateFee.includes(demandId);
    return true;
  };

  for (const p of input.payments) {
    const applyFee = shouldApplyLateFee(p.demandId);
    if (!applyFee) {
      feeStore = feeStore.map((f) =>
        f.demandId === p.demandId && !f.waivedAt
          ? { ...f, waivedAt: new Date().toISOString(), waivedBy: input.actor, waiveReason: "Waived during collection" }
          : f
      );
    }
  }

  const demands = await buildAllDemands();
  const demandMap = new Map(demands.map((d) => [d.id, d]));

  // Allocate oldest due first by default — the UI lets staff override order.
  for (const p of input.payments) {
    const d = demandMap.get(p.demandId);
    if (!d) throw new ApiError("One of the selected demands no longer exists.", 404, "DEMAND_NOT_FOUND");
    if (p.amount <= 0) throw new ApiError(`Amount for ${d.memberName} must be greater than zero.`, 400, "INVALID_AMOUNT");
    if (p.amount > d.balance + 0.01) {
      throw new ApiError(`Payment exceeds the outstanding balance of ${formatMoney(d.balance)} for ${d.memberName}.`, 409, "AMOUNT_EXCEEDS");
    }
  }
  if (input.receivedOn && new Date(input.receivedOn).getTime() > Date.now()) {
    throw new ApiError("Payments cannot be recorded against a future date.", 409, "FUTURE_DATE");
  }
  const total = input.payments.reduce((s, p) => s + p.amount, 0);
  const now = new Date().toISOString();
  const receipt: CollectionReceipt = {
    id: `rcpt-${Date.now()}`,
    receiptNumber: nextReceiptNumber(),
    memberId: input.memberId,
    payments: input.payments,
    total: Math.round(total * 100) / 100,
    mode: input.mode,
    reference: input.reference.trim(),
    receivedOn: input.receivedOn || now,
    remarks: input.remarks.trim(),
    collectedBy: input.actor,
    createdAt: now,
  };
  receiptStore = [receipt, ...receiptStore];
  const views = await buildReceiptViews();
  const view = views.find((v) => v.id === receipt.id)!;
  const pending = (await buildAllDemands()).filter((d) => d.memberId === input.memberId && d.balance > 0);
  return { receipts: [view], pending };
}

export async function getReceipt(id: string): Promise<ReceiptView> {
  if (!USE_MOCK_API) {
    return fetchReceiptView(id);
  }

  await delay(200);
  const views = await buildReceiptViews();
  const view = views.find((v) => v.id === id);
  if (!view) throw new ApiError("Receipt not found.", 404, "RECEIPT_NOT_FOUND");
  return view;
}

export async function listReceipts(range?: { start?: string | null; end?: string | null }): Promise<ReceiptView[]> {
  if (!USE_MOCK_API) {
    const params = new URLSearchParams({ pageSize: "200" });
    if (range?.start) params.set("dateFrom", toDateOnlyString(range.start));
    if (range?.end) params.set("dateTo", toDateOnlyString(range.end));
    const res = await http<{ data: BackendReceipt[] }>(`/payments?${params.toString()}`, { method: "GET", raw: true });
    return (res.data ?? []).map(mapReceipt);
  }

  await delay();
  let views = await buildReceiptViews();
  if (range?.start) views = views.filter((r) => new Date(r.receivedOn) >= new Date(range.start as string));
  if (range?.end) {
    const end = new Date(range.end as string);
    end.setHours(23, 59, 59, 999);
    views = views.filter((r) => new Date(r.receivedOn) <= end);
  }
  return views;
}

export async function reverseReceipt(id: string, reason: string, actor: string): Promise<ReceiptView> {
  if (!USE_MOCK_API) {
    void actor; // the backend records the authenticated user, not a free-text actor name
    await http(`/payments/${id}/reverse`, { method: "POST", body: { reason } });
    return fetchReceiptView(id);
  }

  await delay(400);
  const index = receiptStore.findIndex((r) => r.id === id);
  if (index === -1) throw new ApiError("Receipt not found.", 404, "RECEIPT_NOT_FOUND");
  const existing = receiptStore[index];
  if (existing.reversedAt) throw new ApiError("This receipt is already reversed.", 409, "ALREADY_REVERSED");
  if (!reason.trim()) throw new ApiError("A reversal reason is required.", 400, "REASON_REQUIRED");
  const updated: CollectionReceipt = {
    ...existing,
    reversedAt: new Date().toISOString(),
    reversedBy: actor,
    reversalReason: reason.trim(),
  };
  receiptStore = receiptStore.map((r) => (r.id === id ? updated : r));
  const views = await buildReceiptViews();
  return views.find((v) => v.id === id)!;
}

/* ================= Late fees ================= */

export async function listOverdueDemands(groupId?: string): Promise<CollectionDemand[]> {
  if (!USE_MOCK_API) {
    const url = groupId ? `/late-fees/pending?groupId=${encodeURIComponent(groupId)}` : "/late-fees/pending";
    const rows = await http<BackendLateFeeRow[]>(url, { method: "GET" });
    return rows.map((row) => {
      const balance = Number(row.balance);
      const currentFee = Number(row.currentLateFee);
      return {
        id: row.installmentId,
        groupId: row.groupId ?? "",
        groupCode: row.groupCode,
        groupName: row.groupName,
        cycle: row.cycleNo ?? 0,
        periodLabel: "",
        memberId: "",
        memberCode: row.memberCode,
        memberName: row.fullName,
        memberPhone: "",
        ticketNumber: String(row.ticketNo),
        baseInstallment: 0,
        dividendCredit: 0,
        lateFee: Math.max(0, currentFee || 0),
        netPayable: balance + Math.max(0, currentFee || 0),
        paid: 0,
        balance,
        status: "pending",
        dueDate: row.dueDate,
        daysOverdue: Math.max(0, row.daysLate || 0),
        applicableLateFee: Math.max(0, Number(row.calculatedLateFee) || 0),
        hasLateFeeApplied: Math.max(0, currentFee || 0) > 0,
        wonThisCycle: false,
        groupMemberId: row.groupMemberId,
      };
    });
  }

  await delay(200);
  const demands = await buildAllDemands();
  return demands.filter((d) => d.balance > 0 && d.daysOverdue > 0 && (!groupId || d.groupId === groupId));
}

export async function applyLateFee(demandId: string, amount: number, reason: string, actor: string, groupId?: string): Promise<CollectionDemand[]> {
  if (!USE_MOCK_API) {
    void amount; // the backend computes and records the authoritative fee itself, per each group's own configuration
    void actor;
    void reason; // not persisted per spec — applying a fee doesn't carry a free-text reason field on the backend (waiver does)
    await http("/late-fees/apply", { method: "POST", body: { installmentIds: [demandId] } });
    return listOverdueDemands(groupId);
  }

  await delay(300);
  if (amount <= 0) throw new ApiError("Late fee amount must be greater than zero.", 400, "INVALID_FEE");
  if (!reason.trim()) throw new ApiError("A reason is required to apply a late fee.", 400, "REASON_REQUIRED");
  const active = feeStore.find((f) => f.demandId === demandId && !f.waivedAt);
  if (active) throw new ApiError("A late fee is already applied to this demand.", 409, "FEE_EXISTS");
  feeStore = [
    ...feeStore,
    {
      id: `lfe-${Date.now()}`,
      demandId,
      amount,
      reason: reason.trim(),
      appliedBy: actor,
      appliedAt: new Date().toISOString(),
    },
  ];
  return listOverdueDemands(groupId);
}

export async function waiveLateFee(demandId: string, reason: string, actor: string, groupId?: string): Promise<CollectionDemand[]> {
  if (!USE_MOCK_API) {
    void actor;
    await http(`/installments/${demandId}/waive-late-fee`, { method: "POST", body: { reason } });
    return listOverdueDemands(groupId);
  }

  await delay(300);
  const fee = feeStore.find((f) => f.demandId === demandId && !f.waivedAt);
  if (!fee) throw new ApiError("No active late fee to waive on this demand.", 404, "NO_FEE");
  if (!reason.trim()) throw new ApiError("A waiver reason is required.", 400, "REASON_REQUIRED");
  feeStore = feeStore.map((f) =>
    f.id === fee.id ? { ...f, waivedAt: new Date().toISOString(), waivedBy: actor, waiveReason: reason.trim() } : f
  );
  return listOverdueDemands(groupId);
}

/**
 * Every late-fee application (active and waived) — used by ledger.ts.
 * Stays mock-only regardless of USE_MOCK_API, same reasoning as
 * buildAllDemands(): no real endpoint returns this combined view, and
 * ledger.ts isn't wired to the real API yet.
 */
export async function listLateFeeApplications(): Promise<LateFeeApplication[]> {
  await delay(150);
  return [...feeStore];
}

/** Internal formatting helper (avoids a circular import to the format util). */
function formatMoney(value: number): string {
  return `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export interface SettleDuesMemberPreview {
  installmentId: string;
  memberId: string;
  memberName: string;
  memberCode: string;
  ticketNo: number;
  phone: string;
  dueAmount: string;
  currentBalance: string;
  newBalance: string;
  willOverdraft: boolean;
}

export interface SettleDuesPreviewData {
  cycleId: string;
  cycleNo: number;
  groupName: string;
  groupCode: string;
  periodLabel: string;
  totalPendingMembers: number;
  totalAmountDue: string;
  sufficientCount: number;
  overdraftCount: number;
  members: SettleDuesMemberPreview[];
}

export async function getSettleDuesPreview(cycleId: string): Promise<SettleDuesPreviewData> {
  return http<SettleDuesPreviewData>(`/cycles/${cycleId}/settle-preview`, { method: "GET" });
}

export async function autoDeductCycleAdvance(cycleId: string): Promise<{
  cycleId: string;
  totalDeducted: number;
  count: number;
  deductions: any[];
}> {
  const res = await http<{
    cycleId: string;
    totalDeducted: string | number;
    count: number;
    deductions: any[];
  }>(`/cycles/${cycleId}/auto-deduct`, { method: "POST" });
  return {
    cycleId: res.cycleId,
    totalDeducted: Number(res.totalDeducted),
    count: res.count,
    deductions: res.deductions,
  };
}

export async function closeCycle(cycleId: string): Promise<any> {
  return http(`/cycles/${cycleId}/close`, { method: "POST" });
}

export const collectionService = {
  getCollectionDashboard,
  getCycleCollection,
  getMemberPendingDemands,
  recordPayment,
  getReceipt,
  listReceipts,
  reverseReceipt,
  listOverdueDemands,
  applyLateFee,
  waiveLateFee,
  listLateFeeApplications,
  autoDeductCycleAdvance,
  getSettleDuesPreview,
  closeCycle,
};

export type { Member, DemandPayment };
